import React, { useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Typography, Space, Button, Form } from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import {
  Table,
  ApplicationUIContext,
  Drawer,
  DrawerContent,
  useDrawerState,
} from '@yuhuinc/ui-library';
import styled from 'styled-components';
import { getTranslatedString } from 'helpers/i18n';
import AddIncentive from './AddIncentive';
import CreateIncentive from './AddIncentive/CreateIncentive';

const { Title } = Typography;

const StyledSpace = styled(Space)`
  width: 100%;

  .ant-picker {
    width: 100%;
  }
`;

const RentalIncentivesTableRowActions = ({ incentive, onUpdate, onDelete, disabled }) => {
  const [, editIncentiveDrawer] = useDrawerState(false);

  // Can only edit one-time incentives during creation
  const editable = incentive.id.toString().startsWith('new');

  function onEditIncentive(values) {
    onUpdate(values);
    editIncentiveDrawer.close();
  }

  return (
    <>
      <Space size="middle">
        <Button
          type="link"
          icon={<EditOutlined />}
          onClick={editIncentiveDrawer.open}
          disabled={disabled || !editable}
        />
        <Button type="link" icon={<DeleteOutlined />} onClick={onDelete} disabled={disabled} />
      </Space>
      <Drawer
        title={getTranslatedString(
          'landlordPortal.applicationManagement.inviteLeadToApply.additionalInformation.rentalIncentives.editIncentive.drawerTitle'
        )}
        visible={editIncentiveDrawer.isVisible}
        closable
        destroyOnClose
        onClose={editIncentiveDrawer.close}
      >
        <DrawerContent>
          <CreateIncentive
            initialValues={incentive}
            onSubmit={onEditIncentive}
            onCancel={editIncentiveDrawer.close}
          />
        </DrawerContent>
      </Drawer>
    </>
  );
};

const RentalIncentivesTable = ({ incentives, operations, disabled }) => {
  const { isMobile } = useContext(ApplicationUIContext);
  const columns = [
    {
      title: getTranslatedString('landlordPortal.incentives.internalName'),
      dataIndex: 'internal_name',
      key: 'internal_name',
      width: '45%',
      render(_, row) {
        return (
          <Form.Item shouldUpdate>
            {({ getFieldValue }) => {
              const rawValue = getFieldValue(['incentives', row.name, 'internal_name']);
              if (!rawValue) {
                return 'One-time incentive';
              }
              return rawValue;
            }}
          </Form.Item>
        );
      },
    },
    {
      title: getTranslatedString('landlordPortal.incentives.internalCode'),
      dataIndex: 'internal_code',
      key: 'internal_code',
      width: '45%',
      render(_, row) {
        return (
          <Form.Item shouldUpdate>
            {({ getFieldValue }) => {
              return getFieldValue(['incentives', row.name, 'internal_code']);
            }}
          </Form.Item>
        );
      },
    },
    {
      key: 'actions',
      render(_, row) {
        return (
          <Form.Item shouldUpdate>
            {({ getFieldValue, setFields }) => {
              return (
                <RentalIncentivesTableRowActions
                  incentive={getFieldValue(['incentives', row.name])}
                  onUpdate={(value) => setFields([{ name: ['incentives', row.name], value }])}
                  onDelete={() => operations.remove(row.name)}
                  disabled={disabled}
                />
              );
            }}
          </Form.Item>
        );
      },
    },
  ];

  return (
    <Table
      dataSource={incentives}
      columns={columns}
      hideActionAndSearch
      size={isMobile ? 'middle' : 'small'}
      scroll={{
        x: isMobile,
      }}
      rowKey={(record) => record.key}
      pagination={false}
    />
  );
};

const RentalIncentives = ({ selectedUnit, disabled }) => {
  const { isMobile } = useContext(ApplicationUIContext);
  const form = Form.useFormInstance();
  const [, addIncentiveDrawer] = useDrawerState(false);
  const values = Form.useWatch([], form);

  const canAddIncentive = useMemo(() => {
    return (
      !!values &&
      !!values.lease_term &&
      !!values.lease_start_date &&
      !!values.lease_end_date &&
      !disabled
    );
  }, [values, disabled]);

  return (
    <>
      <Form.List name="incentives">
        {(fields, operations) => {
          const onAddIncentive = (incentive) => {
            operations.add(incentive);
          };

          return (
            <>
              <StyledSpace direction="vertical" size="middle">
                <Title level={isMobile ? 4 : 5} style={{ margin: 0 }}>
                  {getTranslatedString(
                    'landlordPortal.applicationManagement.inviteLeadToApply.additionalInformation.rentalIncentives'
                  )}
                </Title>
                {fields.length > 0 && (
                  <RentalIncentivesTable
                    form={form}
                    incentives={fields}
                    operations={operations}
                    disabled={disabled}
                  />
                )}

                <Button
                  size={isMobile && 'large'}
                  onClick={addIncentiveDrawer.open}
                  disabled={!canAddIncentive}
                >
                  {getTranslatedString(
                    'landlordPortal.applicationManagement.inviteLeadToApply.additionalInformation.rentalIncentives.addIncentive'
                  )}
                </Button>
              </StyledSpace>
              <Drawer
                title={getTranslatedString(
                  'landlordPortal.applicationManagement.inviteLeadToApply.additionalInformation.rentalIncentives.addIncentive.drawerTitle'
                )}
                visible={addIncentiveDrawer.isVisible}
                closable
                destroyOnClose
                onClose={addIncentiveDrawer.close}
              >
                <AddIncentive
                  selectedUnit={selectedUnit}
                  onAdd={onAddIncentive}
                  closeDrawer={addIncentiveDrawer.close}
                />
              </Drawer>
            </>
          );
        }}
      </Form.List>
    </>
  );
};

RentalIncentives.propTypes = {
  selectedUnit: PropTypes.object,
  disabled: PropTypes.bool,
};

export default RentalIncentives;
